// Control/Button (Figma 11:100). Mono, uppercase, tracked — the case comes
// from `.type-label` / `.type-labelSm`, so callers pass normal prose.
//
// `primary` fills with --accent-solid and NOT --accent-fill: the lighter
// fill does not let the cream --accent-ink label clear AA.
//
// THREE ELEMENTS, ONE LOOK. Most drawn actions in the 23 screens are really
// navigation ("VIEW DOCUMENTS", "SWITCH WORKSPACE", "GO TO INSTALL"), and
// `<Link><Button/></Link>` nests a <button> inside an <a> — invalid HTML and a
// screen-reader trap. Two screen agents each forked a ~38-line `LinkButton` for
// want of this; both are deleted.
//
//   (default)  -> <button>
//   `to`       -> router <Link>, a client navigation. Needs a router in
//                 context; unit tests use `src/testing/routed.tsx`.
//   `href`     -> plain <a>, a full document load. For destinations the router
//                 does not own.
//
// `ui.css` already sets `text-decoration: none` on `.maisight-ui-button`, which
// is the primitive having anticipated the anchor cases.

import "./ui.css";
import { Link } from "@tanstack/react-router";
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactNode,
} from "react";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "md" | "sm";

type ButtonOwnProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Stretch to the container, as the drawn "EMAIL ME A LINK" button does. */
  block?: boolean;
  /** Rendered before the label — pass an <Icon />, which is how Figma swaps them in. */
  leading?: ReactNode;
  children: ReactNode;
  className?: string;
};

export type ButtonProps = ButtonOwnProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className"> & {
    to?: undefined;
    href?: undefined;
  };

export type ButtonLinkProps = ButtonOwnProps & {
  /** In-app destination — renders a router `<Link>`. */
  to: string;
  search?: Record<string, unknown>;
  href?: undefined;
};

export type ButtonAnchorProps = ButtonOwnProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "className"> & {
    href: string;
    to?: undefined;
  };

function chrome({ variant, size, block, className }: ButtonOwnProps) {
  return {
    "data-variant": variant ?? "secondary",
    "data-size": size ?? "md",
    "data-block": block ? "true" : undefined,
    className: [
      "maisight-ui-button",
      (size ?? "md") === "sm" ? "type-labelSm" : "type-label",
      className,
    ]
      .filter(Boolean)
      .join(" "),
  };
}

export function Button(
  props: ButtonProps | ButtonLinkProps | ButtonAnchorProps,
) {
  const { leading, children } = props;
  const shared = chrome(props);

  if (props.to !== undefined) {
    const { to, search } = props;
    return (
      <Link {...shared} to={to} search={search}>
        {leading}
        {children}
      </Link>
    );
  }

  if (props.href !== undefined) {
    const {
      variant: _v,
      size: _s,
      block: _b,
      leading: _l,
      children: _c,
      className: _n,
      to: _t,
      ...rest
    } = props;
    return (
      <a {...rest} {...shared}>
        {leading}
        {children}
      </a>
    );
  }

  const {
    variant: _v,
    size: _s,
    block: _b,
    leading: _l,
    children: _c,
    className: _n,
    to: _t,
    href: _h,
    type = "button",
    ...rest
  } = props;
  return (
    <button {...rest} {...shared} type={type}>
      {leading}
      {children}
    </button>
  );
}
